import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import SydButton from '../../../../../../../commonDesign/Button'
import Icon from '../../../../../../../atoms/Icon'
import { useEvalRunForm } from '../../EvalRunFormContext'
import { useProposalCardStyles, useAutoApplyEffect } from './cardCommon'
import ProposalReviewModal from './ProposalReviewModal'

const PROPOSAL_NAME = 'propose_metric'

function ProposedMetricCard ({ toolCallId, args, onApply, autoAccept }) {
  const classes = useProposalCardStyles()
  const { metrics, appliedProposalIds } = useEvalRunForm()
  const isApplied = !!toolCallId && appliedProposalIds.has(toolCallId)

  const [state, setState] = useState(isApplied ? 'applied' : 'pending')
  const [showModal, setShowModal] = useState(false)

  const currentMetric = useMemo(() => (
    metrics.find(m => m.name === args?.name) || null
  ), [metrics, args?.name])

  const proposedMetric = useMemo(() => ({
    name: args?.name,
    type: args?.type,
    description: args?.description,
    weight: args?.weight
  }), [args])

  const apply = useCallback(({ auto = false } = {}) => {
    if (onApply) onApply({ tool: PROPOSAL_NAME, toolCallId, args })
    setState(auto ? 'auto-applied' : 'applied')
    setShowModal(false)
  }, [onApply, toolCallId, args])

  useAutoApplyEffect({ autoAccept, state, apply })

  if (state === 'applied' || state === 'auto-applied') {
    return (
      <div className={`${classes.root} ${classes.applied}`}>
        <div className={classes.appliedRow}>
          <Icon name='check' customSize='16px' />
          <span>{state === 'auto-applied' ? 'Metric auto-applied' : 'Metric applied'}: {args.name}</span>
        </div>
      </div>
    )
  }

  if (state === 'dismissed') {
    return (
      <div className={`${classes.root} ${classes.dismissed}`}>
        <div className={classes.dismissedRow}>
          Metric proposal dismissed: {args.name}
        </div>
      </div>
    )
  }

  const isUpdate = !!currentMetric
  const summary = isUpdate
    ? `Update metric ${args.name} (${args.type}, weight ${args.weight})`
    : `Add metric ${args.name} (${args.type}, weight ${args.weight})`

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <span className={classes.title}>{isUpdate ? 'Proposed metric update' : 'Proposed metric'}</span>
        </div>
        <div className={classes.summary}>{summary}</div>
        {args.reasoning && <div className={classes.reasoning}>{args.reasoning}</div>}
        <div className={classes.divider} />
        <div className={classes.actions}>
          <SydButton size='xs' variant='outline' onClick={() => setState('dismissed')}>
            Dismiss
          </SydButton>
          {onApply && (
            <SydButton size='xs' variant='ai' onClick={() => setShowModal(true)}>
              Review
            </SydButton>
          )}
        </div>
      </div>

      {showModal && (
        <ProposalReviewModal
          flavor='metric'
          title={isUpdate ? `Review metric update: ${args.name}` : `Review new metric: ${args.name}`}
          reasoning={args.reasoning}
          metricCurrent={currentMetric}
          metricProposed={proposedMetric}
          metricMode='upsert'
          onAccept={() => apply()}
          onReject={() => setShowModal(false)}
        />
      )}
    </>
  )
}

ProposedMetricCard.propTypes = {
  toolCallId: PropTypes.string,
  args: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    description: PropTypes.string,
    weight: PropTypes.number,
    reasoning: PropTypes.string
  }).isRequired,
  onApply: PropTypes.func,
  autoAccept: PropTypes.bool
}

export default ProposedMetricCard
